/**
 * "Which heavy `POLYLINE` did this command decline, and what does the user see
 * about it?" — the audit LINES for every heavy-polyline refusal the shell
 * reducer raises.
 *
 * Split out of `./geometryShellReducer.ts` (@codex PR #785 round 14) when the
 * OFFSET and ARRAYPATH refusals joined TRIM / EXTEND's and pushed that file
 * past its 2500-line `max-lines` cap. Pure projection: each function turns a
 * verdict from `command-engine`'s own predicate into ONE localised string, and
 * nothing here decides WHETHER a command declines — that stays the single
 * `command-engine` truth the document reducers read
 * (`heavyPolylineTrim.ts` for TRIM / EXTEND, `heavyPolylineCurveRefusal.ts` for
 * OFFSET / ARRAYPATH), so the shell can never report a refusal the document did
 * not make nor stay silent about one it did.
 *
 * ARRAYPATH's eligibility has TWO halves and both are projected here, from two
 * SEPARATE `command-engine` predicates: the SUBTYPE half
 * (`heavyPolylineCurveRefusal.ts` → {@link arrayPathHeavyPolylineRefusalAudit})
 * and the FRAME half (`arrayPathPlaneRefusal.ts` →
 * {@link arrayPathPlaneRefusalAudit}). They are deliberately not merged into one
 * verdict: a tilted CIRCLE is not a statement about heavy polylines, and
 * `HeavyPolylineArrayPathRefusal`'s switch stays exhaustive over its own two
 * members. @codex PR #785 round 16, P2.
 */

import type {
  ArrayPathPlaneRefusal,
  HeavyPolylineArrayPathRefusal,
  HeavyPolylineExplodeRefusalSummary,
  HeavyPolylineOffsetRefusal,
  HeavyPolylineTrimRefusalSummary,
} from "@dxf-ai-editor/command-engine";

import { t } from "../i18n";

/**
 * The user-facing line for a TRIM / EXTEND dispatch that declined part of its
 * heavy-POLYLINE input, or `null` when it declined nothing.
 *
 * @codex PR #785 round 4, P2 — before this, a heavy polyline picked as the
 * target or as a cutting/boundary edge produced NO intersections and NO
 * mutation while the command still reported plain success. CLAUDE.md
 * ПРИОРИТЕТ #1 allows a known-incomplete subsystem only when it is DOCUMENTED
 * and VISIBLE, so each declined subtype gets its own warning-tone row (the
 * same shape the SCALE x Table refusal uses) instead of a silent no-op.
 *
 * ONE validity truth with the document reducer: the summary comes from
 * `command-engine/heavyPolylineTrim.ts`, which `geometryReducerEditTrim.ts`'s
 * own target arm calls through `trimHeavyPolylineTarget` /
 * `extendHeavyPolylineTarget` — the shell can never report a refusal the
 * document did not make, nor stay silent about one it did.
 *
 * A declined TARGET wins over declined EDGES: the command did nothing at all
 * in that case, which is the more important thing to say.
 */
export function heavyPolylineRefusalAudit(
  refusal: HeavyPolylineTrimRefusalSummary,
): string | null {
  switch (refusal.targetRefusal) {
    case "mesh":
      return t("audit.trim_declined_heavy_polyline.mesh");
    case "polyline-3d":
      return t("audit.trim_declined_heavy_polyline.polyline_3d");
    case "fit":
      return t("audit.trim_declined_heavy_polyline.fit");
    default:
      break;
  }
  if (refusal.refusedEdgeIds.length > 0) {
    return t("audit.trim_heavy_polyline_edges_declined", {
      n: refusal.refusedEdgeIds.length,
    });
  }
  return null;
}

/**
 * The user-facing line for an `OFFSET` dispatch whose target is a heavy
 * `POLYLINE` subtype the command declines, or `null` when it declines nothing.
 *
 * @codex PR #785 round 14, P2 ("Support 2D heavy polylines in OFFSET"). Reads
 * the SAME predicate the document reducer's own guard does
 * (`command-engine/heavyPolylineCurveRefusal.ts`), so the shell can never
 * report a refusal the document did not make, nor stay silent about one it did
 * — the two-site shape `tableScaleRefusal.ts` established and
 * {@link heavyPolylineRefusalAudit} already uses for TRIM / EXTEND.
 *
 * Each subtype gets its OWN row because the reasons are genuinely different:
 * a mesh and a 3D polyline are what AutoCAD ITSELF declines ("Cannot offset
 * that object."), while a fitted polyline is a documented known-incomplete
 * subsystem here. Collapsing them into one line would hide which of the two a
 * user is looking at.
 */
export function offsetHeavyPolylineRefusalAudit(
  refusal: HeavyPolylineOffsetRefusal | null,
): string | null {
  switch (refusal) {
    case "mesh":
      return t("audit.offset_declined_heavy_polyline.mesh");
    case "polyline-3d":
      return t("audit.offset_declined_heavy_polyline.polyline_3d");
    case "fit":
      return t("audit.offset_declined_heavy_polyline.fit");
    case null:
      return null;
  }
}

/**
 * The same, for an `ARRAYPATH` dispatch whose PATH CURVE is a declined heavy
 * `POLYLINE` subtype (@codex PR #785 round 14, P2, "Accept 2D heavy polylines
 * as ARRAYPATH curves").
 *
 * Exhaustive over TWO cases, not three: `HeavyPolylineArrayPathRefusal` does
 * not include `"fit"` at the type level, because ARRAYPATH only READS the curve
 * and samples a curve-fit / spline-fit polyline's drawn approximation like any
 * other. A third arm here would point at a message that could never be shown.
 */
export function arrayPathHeavyPolylineRefusalAudit(
  refusal: HeavyPolylineArrayPathRefusal | null,
): string | null {
  switch (refusal) {
    case "mesh":
      return t("audit.arraypath_declined_heavy_polyline.mesh");
    case "polyline-3d":
      return t("audit.arraypath_declined_heavy_polyline.polyline_3d");
    case null:
      return null;
  }
}

/**
 * The user-facing line for an `ARRAYPATH` dispatch whose path curve is not
 * confined to a plane parallel to WCS XY, or `null` when it is (@codex PR #785
 * round 16, P2, "Preserve Z when sampling tilted array paths").
 *
 * The FRAME half of the same eligibility {@link arrayPathHeavyPolylineRefusalAudit}
 * reports the SUBTYPE half of, and a separate function for the reason the
 * predicates are separate: a tilted CIRCLE / ARC / LWPOLYLINE has nothing to do
 * with heavy polylines, so folding a third member into
 * `HeavyPolylineArrayPathRefusal` would make that type say something it does not
 * mean. Both rows can be reached by the same command; `geometryShellReducer.ts`
 * asks the subtype half first, in the order `arrayReducers.ts`'s own guards run,
 * so the row the user reads is the reason the document reducer actually stopped
 * at.
 *
 * ONE validity truth with the document reducer: the verdict comes from
 * `command-engine/arrayPathPlaneRefusal.ts`, the same predicate
 * `arrayReducers.ts::handleCreateArrayPath` guards on — the shell can never
 * report a refusal the document did not make, nor stay silent about one it did.
 * `apps/web/.../scene/draftModifyStateMachine.ts`'s `arraypath-path` pick step
 * reads this SAME function, so the command line explains the pick in the very
 * words the audit ring would have used.
 *
 * Exhaustive over the predicate's two members. Each gets its OWN row because a
 * user looking at one is not looking at the other: `"tilted-plane"` is a group-210
 * extrusion that lifts the stored OCS coordinates out of XY, `"sloped-line"` is a
 * LINE whose two group-30 elevations differ — different fields, different fix.
 */
export function arrayPathPlaneRefusalAudit(
  refusal: ArrayPathPlaneRefusal | null,
): string | null {
  switch (refusal) {
    case "tilted-plane":
      return t("audit.arraypath_declined_tilted_plane");
    case "sloped-line":
      return t("audit.arraypath_declined_sloped_line");
    case null:
      return null;
  }
}

/**
 * The user-facing line for the heavy POLYLINEs a `geometry/explode-entities`
 * dispatch left whole, or `null` when it left none.
 *
 * @codex PR #785 round 14, FINDING 2. Unlike the INSERT refusal above, this one
 * is PER-ENTITY: the dispatch still explodes every decomposable polyline in the
 * selection, so the row says how many objects were KEPT and why, not that the
 * command did nothing. Both rows can appear in the same dispatch, and the
 * all-or-nothing INSERT refusal wins — it is the more serious statement, since
 * there the command really did nothing to that object's whole content.
 *
 * ONE validity truth with the document reducer: the summary comes from
 * `command-engine/explodeHeavyPolyline.ts`, whose
 * `explodeHeavyPolylineMembers` returns `[]` for exactly the polylines
 * `heavyPolylineExplodeRefusal` declines — so the shell can never report a
 * refusal the reducer did not make, nor stay silent about one it did.
 *
 * The mesh subtypes lead: AutoCAD explodes a polygon / poly-face mesh into
 * `3DFACE` records, a primitive this editor does not have, and that is the
 * substantive gap. `no-drawable-run` is a malformed-record case (fewer than two
 * VISIBLE records — see `heavyPolylineVisibleIndices` for why a spline-fit
 * polyline's frame does not count) and is reported last.
 */
export function heavyPolylineExplodeRefusalAudit(
  refusal: HeavyPolylineExplodeRefusalSummary,
): string | null {
  if (refusal.refusedPolygonMeshCount > 0) {
    return t("audit.explode_declined_polygon_mesh", {
      n: refusal.refusedPolygonMeshCount,
    });
  }
  if (refusal.refusedPolyFaceMeshCount > 0) {
    return t("audit.explode_declined_polyface_mesh", {
      n: refusal.refusedPolyFaceMeshCount,
    });
  }
  if (refusal.refusedNoDrawableRunCount > 0) {
    return t("audit.explode_declined_polyline_no_run", {
      n: refusal.refusedNoDrawableRunCount,
    });
  }
  return null;
}
